import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import React from "react";

interface CouponCardProps {
  internalName: string;
  active: boolean;
  discountTypeLabel: string;
  createdAtLabel: string;
  updatedAtLabel: string;
  discountValueLabel: string;
  minOrderAmountLabel: string;
  validityLabel: string;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  editDisabled: boolean;
  deleteDisabled: boolean;
  editLabel: string;
  deleteLabel: string;
  editLoading: boolean;
  deleteLoading: boolean;
  children: React.ReactNode;
}

export const CouponCard: React.FC<CouponCardProps> = ({
  internalName,
  active,
  discountTypeLabel,
  createdAtLabel,
  updatedAtLabel,
  discountValueLabel,
  minOrderAmountLabel,
  validityLabel,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  editDisabled,
  deleteDisabled,
  editLabel,
  deleteLabel,
  editLoading,
  deleteLoading,
  children,
}) => {
  const handleEditClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    onEdit();
  };

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    onDelete();
  };

  const handleFocus = (event: React.FocusEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  return (
    <Accordion
      disableGutters
      square={false}
      expanded={expanded}
      onChange={(_, isExpanded) => onToggle(isExpanded)}
      sx={{
        borderRadius: 3,
        boxShadow: "none",
        //border: "1px solid rgba(0, 0, 0, 0.1)",
        backgroundColor: grey[50],
        transition: "background-color 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          backgroundColor: "rgba(30,160,151,0.08)",
          borderColor: "rgba(30,160,151,0.35)",
        },
        "&::before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 2.5 },
          "& .MuiAccordionSummary-content": {
            marginLeft: 0,
            width: "100%",
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            ml: 2,
          },
        }}
      >
        <Stack spacing={{ xs: 1.5 }} width="100%">

          <Stack
            direction={{ xs: "row", md: "row" }}
            spacing={{ xs: 1.5 }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            sx={{ color: "text.secondary" }}
          >
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              flexWrap={'wrap'}
              gap={1}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ wordBreak: "break-word" }}
              >
                {internalName}
              </Typography>

              <Chip
                label={active ? "Ativo" : "Inativo"}
                size="small"
                color={active ? "success" : "default"}
                variant={active ? "filled" : "outlined"}
                sx={{ fontWeight: 600, width: "fit-content" }}
              />
            </Box>

            <Box
              display="flex"
              justifyContent="flex-end"
              flexWrap="wrap"
              gap={1}
            >
              <Tooltip title={editLabel} disableInteractive>
                <Box component="span">
                  <IconButton
                    size="small"
                    onClick={handleEditClick}
                    onFocus={handleFocus}
                    disabled={editDisabled}
                    color="primary"
                    aria-label={editLabel}
                  >
                    {editLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <EditOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </Tooltip>

              <Tooltip title={deleteLabel} disableInteractive>
                <Box component="span">
                  <IconButton
                    size="small"
                    onClick={handleDeleteClick}
                    onFocus={handleFocus}
                    disabled={deleteDisabled || active}
                    color="error"
                    aria-label={deleteLabel}
                  >
                    {deleteLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <DeleteOutlineIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </Tooltip>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1.5 }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            sx={{ color: "text.secondary" }}
          >

            <Typography variant="subtitle1" sx={{ color: "error.main", fontWeight: 600 }}>
              {discountTypeLabel + discountValueLabel}
            </Typography>

            <Typography variant="body2">
              Período:{" "}
              <Box component="span" fontWeight={600}>
                {validityLabel}
              </Box>
            </Typography>

          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1.5 }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            sx={{ color: "text.secondary" }}
          >
            <Typography variant="body2">
              Pedido mínimo:{" "}
              <Box component="span" fontWeight={600}>
                {minOrderAmountLabel}
              </Box>
            </Typography>

            <Typography variant="body2">
              Atualizado em{" "}
              <Box component="span" fontWeight={600}>
                {updatedAtLabel}
              </Box>
            </Typography>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: { xs: 2, md: 3 },
          pb: { xs: 2, md: 3 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};
